// Epoch 19: Lead Generator Enhancements - Tier Selection Category Component
// File: src/components/filters/TierSelectionCategory.tsx  
// Purpose: Multi-select tier filtering with pricing transparency and quality descriptions

import React, { useState } from 'react';
import { TierSelectionCategoryProps, TierInfo } from '../../types/filters';

const TIER_INFO: TierInfo[] = [
  {
    tier: '1',
    label: 'Tier 1 (Premium)',
    description: 'Highest quality - Verified contact info, recent activity',
    upcharge: 0.10,
    scoreRange: { min: 90, max: 100 }
  },
  {
    tier: '2',
    label: 'Tier 2 (High)',
    description: 'High quality - Strong profile data, good match likelihood',
    upcharge: 0.08,
    scoreRange: { min: 80, max: 89 }
  },
  {
    tier: '3',
    label: 'Tier 3 (Good)',
    description: 'Good quality - Complete profiles, moderate match likelihood',
    upcharge: 0.05,
    scoreRange: { min: 70, max: 79 }
  },
  {
    tier: '4',
    label: 'Tier 4 (Standard)',
    description: 'Standard quality - Basic profile data, lower cost option',
    upcharge: 0.03,
    scoreRange: { min: 60, max: 69 }
  },
  {
    tier: '5',
    label: 'Tier 5 (Economy)',
    description: 'Economy quality - Minimal data, budget-friendly option',
    upcharge: 0.01,
    scoreRange: { min: 0, max: 59 }
  }
];

export default function TierSelectionCategory({
  selectedTiers,
  onToggleTier,
  onClearTiers
}: TierSelectionCategoryProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleSelectAll = () => {
    TIER_INFO.forEach(tier => {
      if (!selectedTiers.includes(tier.tier)) {
        onToggleTier(tier.tier);
      }
    });
  };

  const handleClearSelection = () => {
    onClearTiers();
  };

  // Calculate total upcharge and average
  const totalUpcharge = TIER_INFO
    .filter(tier => selectedTiers.includes(tier.tier))
    .reduce((sum, tier) => sum + tier.upcharge, 0);

  const averageUpcharge = selectedTiers.length > 0 ? totalUpcharge / selectedTiers.length : 0;

  return (
    <div className="border-b border-gray-100 pb-4">
      {/* Category Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
        >
          <svg 
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span>Lead Quality Tiers</span>
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
            {selectedTiers.length}/5
          </span>
        </button>
        
        {selectedTiers.length < 5 && (
          <button
            onClick={handleClearSelection}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Reset
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="space-y-3">
          {/* Tier Selection Summary */}
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="text-sm text-blue-800">
              <div className="font-medium mb-1">Selected Tiers Impact:</div>
              <div className="space-y-1">
                <div>Average upcharge: +${averageUpcharge.toFixed(3)} per lead</div>
                <div>Quality range: {selectedTiers.length === 5 ? 'All quality levels' : `${selectedTiers.length} tiers selected`}</div>
                {selectedTiers.length > 0 && (
                  <div className="text-xs text-blue-600 mt-2">
                    Selected: {selectedTiers.sort().map(tier => `Tier ${tier}`).join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Select All / Clear All */}
          <div className="flex items-center space-x-2 text-xs">
            <button
              onClick={handleSelectAll}
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              Select All Tiers
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={handleClearSelection}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear Selection
            </button>
          </div>

          {/* Tier Options */}
          <div className="space-y-2">
            {TIER_INFO.map((tierInfo) => (
              <label
                key={tierInfo.tier}
                className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded border border-gray-100 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedTiers.includes(tierInfo.tier)}
                  onChange={() => onToggleTier(tierInfo.tier)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1 transition-colors"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {tierInfo.label}
                    </span>
                    <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                      +${tierInfo.upcharge.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {tierInfo.description}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Score range: {tierInfo.scoreRange.min}-{tierInfo.scoreRange.max}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            💡 <strong>Tip:</strong> Select higher tiers for better quality leads with verified contact information. 
            All tiers are selected by default for maximum lead diversity.
          </div>
        </div>
      )}
    </div>
  );
}